import {
  Box,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../features/axiosInstance/axiosInstance";
import { ROUTES } from "../routes/routes";

const EmailConfirmedPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        const userId = searchParams.get("userId");
        const token = searchParams.get("token");

        if (!userId || !token) {
          setError("Missing required parameters for email confirmation");
          setIsLoading(false);
          return;
        }

        const response = await axiosInstance.get("/account/confirmemail", {
          params: {
            userId,
            token,
          },
        });

        if (response.status === 200) {
          const email = localStorage.getItem("pendingConfirmationEmail");
          setConfirmedEmail(email);
          localStorage.removeItem("pendingConfirmationEmail");

          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate(ROUTES.LOGIN);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return;
        } else {
          setError("Failed to confirm email. Please try again.");
        }
      } catch (error: any) {
        setError(
          error.response?.data?.message ||
            "An error occurred during email confirmation"
        );
      } finally {
        setIsLoading(false);
      }
      return;
    };

    confirmEmail();
  }, [navigate, searchParams]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: `calc(100vh - var(--header-height, 72px))`,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            width: "100%",
          }}
        >
          {isLoading && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <CircularProgress size={48} sx={{ color: "primary.main" }} />
              </Box>
              <Typography variant="h6" align="center" sx={{ color: "text.secondary" }}>
                Confirming your email address...
              </Typography>
            </>
          )}

          {!isLoading && error && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "rgba(239,68,68,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ErrorIcon sx={{ width: 40, height: 40, color: "error.main" }} />
                </Box>
              </Box>
              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Email Confirmation Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            </>
          )}

          {!isLoading && !error && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "rgba(34,197,94,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleIcon sx={{ width: 40, height: 40, color: "success.main" }} />
                </Box>
              </Box>
              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Email Confirmed Successfully!
              </Typography>
              <Typography variant="body1" align="center" sx={{ mb: 3, color: "text.secondary" }}>
                Your email address
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    backgroundColor: "rgba(14,134,212,0.06)",
                    color: "primary.main",
                    fontWeight: 500,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    display: "inline-block",
                    textAlign: "center",
                  }}
                >
                  {confirmedEmail || "your@email.com"}
                </Box>{" "}
                has been confirmed.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to login in {countdown} seconds...
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={20} sx={{ color: "primary.main" }} />
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailConfirmedPage;
